/**
 * Compliance Service - API integration for Advanced Compliance Engine
 */

import api from '../shared/services/api';

export interface ComplianceCheckRequest {
  document_id?: string;
  shipment_data?: any;
  entities: Array<{
    name: string;
    type: string;
    country?: string;
  }>;
  products: Array<{
    description: string;
    hs_code?: string;
    value?: number;
    quantity?: number;
    origin_country?: string;
  }>;
  countries: string[];
  check_types?: string[];
  fast_track?: boolean;
  include_recommendations?: boolean;
}

export interface ComplianceReport {
  report_id: string;
  document_id?: string;
  created_at: string;
  overall_status: 'compliant' | 'non_compliant' | 'requires_review' | 'conditional' | 'pending';
  sanctions_check?: any[];
  license_check?: any[];
  dual_use_check?: any[];
  aml_check?: any[];
  product_safety_check?: any[];
  risk_assessment: {
    overall_risk_score: number;
    risk_score?: number; // Backend uses this name
    risk_level: 'critical' | 'high' | 'medium' | 'low' | 'minimal';
    risk_factors: Record<string, number> | string[]; // Backend returns string array
    highest_risks: string[];
    mitigation_measures: string[];
    recommendations?: string[]; // Backend field
    confidence_score: number;
  };
  required_actions: Array<{
    action_type: string;
    description: string;
    priority: string;
    deadline?: string;
    responsible_party?: string;
    documentation_needed: string[];
    estimated_time?: string;
  }>;
  required_documents: string[];
  jurisdictions_checked: string[];
  regulations_checked: string[];
  check_duration_ms: number;
  processing_time_ms?: number; // Backend uses this name
  summary: string;
  recommendations: string[];
}

export interface SanctionCheckResult {
  // Request parameters (what was sent to BEO Sanctions)
  entity_name: string;
  entity_type: string;
  threshold_used?: number;  // Percentage (0-100)
  fuzzy_matching_used?: boolean;

  // Response status
  is_sanctioned: boolean;
  match_score: number;

  // Lists information
  lists_checked: string[];  // All lists that were screened
  matched_lists?: string[];  // Only lists with matches (optional)

  // Detailed match information
  matches?: Array<{
    name?: string;
    score?: number;
    list?: string;
    reason?: string;
    details?: any;
  }>;
  match_count?: number;

  // API metadata
  checked_at: string;
  source?: string;  // "BEO_SANCTIONS_API"
  api_version?: string;  // "V2"
  search_id?: string;  // BEO search ID for traceability
  credential_source?: string;  // "user" or "system"

  // Additional details
  details?: any;
  warning?: string;
}

export interface LicenseRequirement {
  license_type: string;
  jurisdiction: string;
  product_category: string;
  is_required: boolean;
  exemptions: string[];
  application_process?: string;
  processing_time_days?: number;
  validity_period_days?: number;
}

export interface DualUseCheckResult {
  product_description: string;
  hs_code?: string;
  is_dual_use: boolean;
  control_list_number?: string;
  regime?: string;
  end_use_concerns: string[];
  license_requirements: LicenseRequirement[];
}

export interface AMLCheckResult {
  entity_name: string;
  risk_score: number;
  pep_status: boolean;
  adverse_media: boolean;
  high_risk_jurisdiction: boolean;
  suspicious_patterns: string[];
  required_due_diligence: string;
}

// Additional interfaces for regulation features
export interface Regulation {
  code: string;
  name: string;
  jurisdiction: string;
  type: string;
  description: string;
  effective_date: string;
  last_updated?: string;
  severity?: string;
  requirements?: string[];
  penalties?: string[];
  keywords?: string[];
  url?: string;
}

export interface RegulationUpdate {
  code: string;
  name: string;
  jurisdiction: string;
  updated: string;
}

class ComplianceService {
  /**
   * Perform comprehensive compliance check
   */
  async checkCompliance(request: ComplianceCheckRequest) {
    try {
      // If we have entities and products already extracted, use the optimized endpoint
      if (request.entities && request.entities.length > 0) {
        const response = await api.post('/api/v1/compliance/check-extracted-data', {
          entities: request.entities,
          products: request.products || [],
          countries: request.countries || [],
          check_types: request.check_types || ['sanctions', 'license', 'dual_use'],
          document_id: request.document_id
        });
        return response.data;
      }
      // Otherwise use the standard check endpoint
      const response = await api.post('/api/v1/compliance/check', request);
      // The backend returns ComplianceCheckResponse with report nested inside
      const report = response.data.report || response.data;
      
      // Ensure compatibility with frontend expectations
      if (report && report.risk_assessment) {
        // Add missing fields that frontend expects
        report.risk_assessment.highest_risks = report.risk_assessment.highest_risks || [];
        report.risk_assessment.mitigation_measures = report.risk_assessment.mitigation_measures || 
          report.risk_assessment.recommendations || [];
        report.risk_assessment.overall_risk_score = report.risk_assessment.risk_score || 0;
        report.risk_assessment.confidence_score = report.risk_assessment.confidence_score || 75; // Default confidence
      }
      
      // Ensure other expected fields exist
      if (report) {
        report.required_actions = report.required_actions || [];
        report.required_documents = report.required_documents || [];
        report.jurisdictions_checked = report.jurisdictions_checked || [];
        report.regulations_checked = report.regulations_checked || [];
        report.check_duration_ms = report.processing_time_ms || report.check_duration_ms || 0;
      }
      
      return report;
    } catch (error) {
      console.error('Compliance check failed:', error);
      throw error;
    }
  }

  /**
   * Check entity against sanctions lists
   */
  async checkSanctions(
    entityName: string,
    entityType: string = 'company',
    fuzzyMatching: boolean = true,
    threshold: number = 0.85
  ): Promise<SanctionCheckResult> {
    try {
      const response = await api.post('/api/v1/compliance/sanctions/check', {
        entity_name: entityName,
        entity_type: entityType,
        fuzzy_matching: fuzzyMatching,
        threshold: threshold
      });
      return response.data;
    } catch (error) {
      console.error('Sanctions check failed:', error);
      throw error;
    }
  }

  /**
   * Check multiple entities against sanctions
   */
  async checkSanctionsBatch(entities: Array<{ name: string; type: string }>) {
    try {
      const response = await api.post('/api/v1/compliance/sanctions/batch', entities);
      return response.data;
    } catch (error) {
      console.error('Batch sanctions check failed:', error);
      throw error;
    }
  }

  /**
   * Check license requirements
   */
  async checkLicenses(
    product: any,
    destinationCountry: string,
    originCountry: string = 'US',
    transactionType: string = 'export'
  ): Promise<LicenseRequirement[]> {
    try {
      const response = await api.post('/api/v1/compliance/licenses/check', {
        product,
        destination_country: destinationCountry,
        origin_country: originCountry,
        transaction_type: transactionType
      });
      return response.data;
    } catch (error) {
      console.error('License check failed:', error);
      throw error;
    }
  }

  /**
   * Check if product is dual-use
   */
  async checkDualUse(
    product: any,
    endUser?: string,
    endUse?: string
  ): Promise<DualUseCheckResult> {
    try {
      const response = await api.post('/api/v1/compliance/dual-use/check', {
        product,
        end_user: endUser,
        end_use: endUse
      });
      return response.data;
    } catch (error) {
      console.error('Dual-use check failed:', error);
      throw error;
    }
  }

  /**
   * Perform AML screening
   */
  async screenAML(entity: any): Promise<AMLCheckResult> {
    try {
      const response = await api.post('/api/v1/compliance/aml/screen', {
        entity
      });
      return response.data;
    } catch (error) {
      console.error('AML screening failed:', error);
      throw error;
    }
  }

  /**
   * Get applicable regulations
   */
  async getApplicableRegulations(countries: string[], categories: string[] = []) {
    try {
      const params = new URLSearchParams();
      countries.forEach(c => params.append('countries', c));
      categories.forEach(c => params.append('categories', c));
      
      const response = await api.get(`/api/v1/compliance/regulations/applicable?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get regulations:', error);
      throw error;
    }
  }

  /**
   * Get regulation details
   */
  async getRegulationDetails(jurisdiction: string, regulationCode: string) {
    try {
      const response = await api.get(`/api/v1/compliance/regulations/${jurisdiction}/${regulationCode}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get regulation details:', error);
      throw error;
    }
  }

  /**
   * Search regulations
   */
  async searchRegulations(query: string, jurisdiction?: string) {
    try {
      const params = new URLSearchParams({ query });
      if (jurisdiction) {
        params.append('jurisdiction', jurisdiction);
      }
      
      const response = await api.get(`/api/v1/compliance/regulations/search?${params.toString()}`);
      return response.data;
    } catch (error) {
      console.error('Regulation search failed:', error);
      throw error;
    }
  }

  /**
   * Check for regulation updates
   */
  async checkRegulationUpdates() {
    try {
      const response = await api.get('/api/v1/compliance/regulations/updates');
      return response.data;
    } catch (error) {
      console.error('Failed to check regulation updates:', error);
      throw error;
    }
  }

  /**
   * Get compliance history
   */
  async getComplianceHistory(limit: number = 10, offset: number = 0) {
    try {
      const response = await api.get('/api/v1/compliance/history', {
        params: { limit, offset }
      });
      return response.data;
    } catch (error) {
      console.error('Failed to get compliance history:', error);
      throw error;
    }
  }

  /**
   * Get specific compliance report
   */
  async getComplianceReport(reportId: string): Promise<ComplianceReport> {
    try {
      const response = await api.get(`/api/v1/compliance/report/${reportId}`);
      return response.data;
    } catch (error) {
      console.error('Failed to get compliance report:', error);
      throw error;
    }
  }

  /**
   * Update sanctions lists (Admin only)
   */
  async updateSanctionsLists() {
    try {
      const response = await api.post('/api/v1/compliance/sanctions/update-lists');
      return response.data;
    } catch (error) {
      console.error('Failed to update sanctions lists:', error);
      throw error;
    }
  }

  /**
   * Quick compliance check for a document
   */
  async quickCheck(documentData: any) {
    // Extract entities and products from document
    const entities = [];
    const products: any[] = [];
    const countries = [];

    // Extract consignor/consignee
    if (documentData.nachrichtensender) {
      entities.push({ name: documentData.nachrichtensender, type: 'company' });
    }
    if (documentData.nachrichtenempfanger) {
      entities.push({ name: documentData.nachrichtenempfanger, type: 'company' });
    }

    // Extract products if available
    if (documentData.items) {
      documentData.items.forEach((item: any) => {
        products.push({
          description: item.description || '',
          hs_code: item.hs_code,
          value: item.value,
          quantity: item.quantity
        });
      });
    }

    // Default countries for demo
    countries.push('US', 'EU', 'CN');

    const request: ComplianceCheckRequest = {
      entities,
      products,
      countries,
      check_types: ['sanctions', 'license', 'dual_use', 'aml'],
      fast_track: true,
      include_recommendations: true
    };

    return this.checkCompliance(request);
  }

  // ==================== AI-Enhanced Methods ====================

  /**
   * Extract entities from document text using AI (Mistral)
   */
  async extractEntitiesFromDocument(
    text: string,
    documentType?: string
  ): Promise<{
    entities: any[];
    products: any[];
    transaction: any;
    confidence: number;
    extraction_time_ms: number;
  }> {
    try {
      const response = await api.post('/api/v1/compliance/ai/extract-entities', {
        text,
        document_type: documentType
      }, {
        timeout: 45000 // 45 second timeout for AI extraction
      });
      return response.data;
    } catch (error) {
      console.error('Entity extraction failed:', error);
      throw error;
    }
  }

  /**
   * AI-enhanced compliance check with entity extraction and semantic matching
   */
  async checkComplianceWithAI(
    documentText: string,
    documentType?: string,
    options: {
      useSemanticMatching?: boolean;
      extractEntitiesFirst?: boolean;
    } = {}
  ): Promise<ComplianceReport> {
    try {
      const response = await api.post('/api/v1/compliance/ai/compliance-check', {
        document_text: documentText,
        document_type: documentType,
        use_semantic_matching: options.useSemanticMatching !== false,
        extract_entities_first: options.extractEntitiesFirst !== false
      }, {
        timeout: 60000 // 60 second timeout for full compliance check
      });
      return response.data;
    } catch (error) {
      console.error('AI compliance check failed:', error);
      throw error;
    }
  }

  /**
   * Check semantic similarity between two texts using Mistral embeddings
   */
  async checkSemanticSimilarity(
    text1: string,
    text2: string
  ): Promise<{
    text1: string;
    text2: string;
    similarity_score: number;
    match_level: string;
    is_likely_match: boolean;
    confidence: string;
    recommendation: string;
  }> {
    try {
      const response = await api.post('/api/v1/compliance/ai/semantic-match', {
        text1,
        text2
      });
      return response.data;
    } catch (error) {
      console.error('Semantic matching failed:', error);
      throw error;
    }
  }

  /**
   * AI-powered risk assessment using Mistral
   */
  async assessRiskWithAI(
    entities: any[],
    products: any[],
    transaction: any,
    sanctionsResults: any[] = []
  ): Promise<{
    risk_assessment: any;
    report: string;
  }> {
    try {
      const response = await api.post('/api/v1/compliance/ai/risk-assessment', {
        entities,
        products,
        transaction,
        sanctions_results: sanctionsResults
      });
      return response.data;
    } catch (error) {
      console.error('AI risk assessment failed:', error);
      throw error;
    }
  }

  /**
   * Quick compliance check - responds in under 5 seconds
   */
  async quickComplianceCheck(
    documentText: string,
    documentType?: string
  ): Promise<ComplianceReport> {
    try {
      const response = await api.post('/api/v1/compliance/quick-check', {
        document_text: documentText,
        document_type: documentType
      }, {
        timeout: 10000 // 10 second timeout for quick check
      });
      return response.data;
    } catch (error) {
      console.error('Quick compliance check failed:', error);
      throw error; // Propagate error to caller for proper handling
    }
  }

  /**
   * Enhanced sanctions check with semantic matching
   */
  async enhanceSanctionsCheck(
    entityName: string,
    entityType: string = 'company',
    useAliases: boolean = true
  ): Promise<any> {
    try {
      const response = await api.post('/api/v1/compliance/ai/enhance-sanctions-check', {
        entity_name: entityName,
        entity_type: entityType,
        use_aliases: useAliases
      });
      return response.data;
    } catch (error) {
      console.error('Enhanced sanctions check failed:', error);
      throw error;
    }
  }

  /**
   * Poll compliance check status for a file
   * Used for async compliance checks that run in background
   *
   * @param fileId - The uploaded file ID
   * @returns Status object with compliance report when complete
   */
  async getComplianceStatus(fileId: string): Promise<{
    status: 'in_progress' | 'completed' | 'not_found';
    message?: string;
    transaction_id?: string;
    report_id?: string;
    overall_status?: string;
    risk_level?: string;
    risk_score?: number;
    checked_at?: string;
    processing_time_ms?: number;
    report?: ComplianceReport;
  }> {
    try {
      const response = await api.get(`/api/v1/files/${fileId}/compliance-status`);
      return response.data;
    } catch (error) {
      console.error('Failed to get compliance status:', error);
      throw error;
    }
  }

  /**
   * Get complete audit trail for compliance check
   * Shows every step of the compliance pipeline for debugging
   *
   * @param fileId - The uploaded file ID
   * @returns Audit trail with all pipeline steps
   */
  async getComplianceAuditTrail(fileId: string): Promise<{
    report_id: string;
    file_id: string;
    overall_status: string;
    total_steps: number;
    audit_trail: Array<{
      timestamp: string;
      action: string;
      entity: string | null;
      result: string | null;
    }>;
  }> {
    try {
      const response = await api.get(`/api/v1/files/${fileId}/compliance-audit`);
      return response.data;
    } catch (error) {
      console.error('Failed to get compliance audit trail:', error);
      throw error;
    }
  }
}

export const complianceService = new ComplianceService();
export default complianceService;